import * as signalR from '@aspnet/signalr'
import EventBus from '../../eventBus'
import {bufferTime, Subject} from 'rxjs'

const workspaceUpdateSubject = new Subject()

const workspaceUpdateSubscription = workspaceUpdateSubject.asObservable()
  .pipe(bufferTime(1000))
  .subscribe(buffer => {
    if (buffer.length > 0) {
      EventBus.$emit('BATCH_UPDATE_WORKSPACES', buffer)
    }
  })

window.onbeforeunload = function () {
  workspaceUpdateSubscription.unsubscribe()
}

export default {
  namespaced: true,

  state: {
    connection: null,
    retries: 0,
    maxRetry: 10,
    loopTimer: null
  },

  mutations: {
    init(state, viewerId) {
      if (state.connection) {
        return
      }

      state.connection = new signalR.HubConnectionBuilder()
        .configureLogging(signalR.LogLevel.Information)
        .withUrl(getSignalRUrl(viewerId), {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        })
        .build()

      registerCallbacks(state.connection)
    },
    saveLoopTimer(state, loopTimer) {
      if (!state.loopTimer) {
        console.debug('Clearing the previous timer ...')

        // INFO: Make sure to clear the previous timer.
        clearTimeout(state.loopTimer)
      }

      state.loopTimer = loopTimer
    }
  },

  actions: {
    async start({commit, dispatch, state}, viewerId) {
      if (!viewerId) {
        throw new Error('The given viewerId was not defined.')
      }

      const isNewConnection = !state.connection
      commit('init', viewerId)

      try {
        await state.connection.start()

        EventBus.$emit('LOADING')

        dispatch('askBackendInformation')
        dispatch('askInitialState')

        if (isNewConnection) {
          state.connection.onclose(() => {
            clearTimeout(state.loopTimer)
            setTimeout(() => dispatch('start', viewerId), 1000)
          })
        }

        await loopAskBackendInformation(commit, dispatch)
      } catch (e) {
        EventBus.$emit('SIGNALR_ERROR', e)
        setTimeout(() => dispatch('start', viewerId), 1000)
      }
    },
    async askInitialState({state}) {
      if (!state.connection) {
        throw new Error('The connection is falsy.')
      }

      console.debug('Asking the initial state ...')
      await state.connection.invoke('ask-initial-state')
    },
    async askBackendInformation({state}) {
      if (!state.connection) {
        throw new Error('The connection is falsy.')
      }

      console.debug('Asking the backend information ...')
      await state.connection.invoke('ask-backend-information')
    }
  }
}

async function loopAskBackendInformation(commit, dispatch) {
  const timer = setTimeout(async () => {
      await dispatch('askBackendInformation')
      await loopAskBackendInformation(commit, dispatch)
    },
    1000 * 60 * 60)
  commit('saveLoopTimer', timer)
}

function registerCallbacks(connection) {
  connection.on('registration-pin', (pin) => {
    EventBus.$emit('REGISTRATION_PIN', pin)
  })
  connection.on('registration-accepted', () => {
    EventBus.$emit('REGISTRATION_ACCEPTED')
  })
  connection.on('load-unknown-view', (payload) => {
    EventBus.$emit('LOAD_UNKNOWN_VIEW', payload)
  })
  connection.on('load-view', (payload) => {
    EventBus.$emit('LOAD_VIEW', payload)
  })
  connection.on('organization-disabled-view', () => {
    EventBus.$emit('ORGANIZATION_DISABLED_VIEW')
  })
  connection.on('viewer-deleted', () => {
    EventBus.$emit('VIEWER_DELETED')
  })
  connection.on('backend-information', (payload) => {
    EventBus.$emit('BACKEND_INFORMATION', payload)
  })
  connection.on('floorplan-updated', (payload) => {
    EventBus.$emit('FLOORPLAN_UPDATED', payload)
  })
  connection.on('organization-is-disabled-changed', (payload) => {
    EventBus.$emit('ORGANIZATION_IS_DISABLED_CHANGED', payload)
  })
  connection.on('viewer-updated', (payload) => {
    EventBus.$emit('VIEWER_UPDATED', payload)
  })
  connection.on('visualization-updated', (payload) => {
    EventBus.$emit('VISUALIZATION_UPDATED', payload)
  })
  connection.on('building-updated', (payload) => {
    EventBus.$emit('BUILDING_UPDATED', payload)
  })
  connection.on('workspace-updated', (payload) => {
    EventBus.$emit('WORKSPACE_UPDATED', payload)
  })
  connection.on('error', (payload) => {
    EventBus.$emit('ERROR', payload || 'something went wrong with the connection.')
  })

  connection.on('workspace-presence-update', (payload) => workspaceUpdateSubject.next({
    commitType: 'presence',
    ...payload
  }))

  connection.on('workspace-calendar-updated', (payload) => workspaceUpdateSubject.next({
    commitType: 'calendar',
    ...payload
  }))
}

function getSignalRUrl(viewerId) {
  const params = new URLSearchParams(window.location.search)
  let viewerIdQuery = `roomz-viewer-id=${viewerId}`
  const publicId = 'roomz-public-id'
  if (params.has(publicId)) {
    viewerIdQuery = `${publicId}=${params.get(publicId)}`
  }

  if (!process.env.HOST) {
    return `/viewer-hub?${viewerIdQuery}`
  }

  return `${process.env.HOST}/viewer-hub?${viewerIdQuery}`
}



// WEBPACK FOOTER //
// ./src/store/modules/signalr.js